import { AccountHistory } from '../entities/AccountHistory';
import { AccountRegisterInfo } from '../entities/AccountRegisterInfo';
import { AccountRewardsInfo } from '../entities/AccountRewardsInfo';
import { ActivityHistory } from '../entities/ActivityHistory';
import { RequestClaimMissingPointsDTO, ResponseClaimMissingPointsDTO } from '../entities/MissingPointsFormsDTO';
import { Store } from '../entities/Store';
import { TransactionHistory } from '../entities/TransactionHistory';
import { DineRewardsService } from '../services';

interface checkPhoneNumberResponse {
	isValid: boolean;
	isAvailable: boolean;
	printedCardNumber: string;
}

export const DineRewardsManager = {
	GetAllStores: async (): Promise<Array<Store> | null> => {
		const response = await DineRewardsService.GetAllStores();
		if (response.error) return null;

		return response.body;
	},
	GetAccountInfo: async (phoneNumber: string): Promise<AccountRewardsInfo | null> => {
		const response = await DineRewardsService.GetAccountInfo(phoneNumber);
		if (response.error) return null;

		return response.body;
	},
	GetTransactionHistoryByEmail: async (email: string): Promise<TransactionHistory | null> => {
		const response = await DineRewardsService.GetTransactionHistoryByEmail(email);
		if (response.error) return null;

		return response.body;
	},
	GetAccountHistoryByPhoneNumber: async (phoneNumber: string): Promise<AccountHistory | null> => {
		const response = await DineRewardsService.GetAccountHistoryByPhoneNumber(phoneNumber);
		if (response.error) return null;

		return response.body;
	},
	GetActivityTimeline: async (loyaltyAccountNumber: string): Promise<Array<ActivityHistory> | null> => {
		const response = await DineRewardsService.GetActivityTimeline(loyaltyAccountNumber);
		if (response.error) return null;

		return response.body;
	},
	CheckPhoneNumberAvailability: async (phoneNumber: string): Promise<checkPhoneNumberResponse> => {
		const response = await DineRewardsService.GetAccountInfo(phoneNumber);
		const isAvailable = response.body.code === 404104;
		const isInvalid = response.body.code === 400403;
		const accountId = response.body.printedCardNumber;
		if (response.error) {
			if (isAvailable) {
				return { isValid: true, isAvailable: true, printedCardNumber: accountId };
			}
			if (isInvalid) {
				return { isValid: false, isAvailable: true, printedCardNumber: '' };
			}
			return { isValid: false, isAvailable: false, printedCardNumber: accountId };
		}
		return { isValid: true, isAvailable: false, printedCardNumber: accountId };
	},
	CheckEmailAvailability: async (email: string): Promise<boolean> => {
		const response = await DineRewardsService.GetAccountInfoByEmail(email);
		const isAvailable = response.body.code === 404104;
		return isAvailable;
	},
	GetGDPRExtensions: async (): Promise<Array<string> | null> => {
		const response = await DineRewardsService.GetGDPRExtensions();
		if (response.error) return null;
		return response.body;
	},
	GetProfanityCheck: async (firstName: string, lastName: string) => {
		const response = await DineRewardsService.GetProfanityCheck(firstName, lastName);
		return response.body;
	},
	PostEditAccountInfo: async (body: AccountRegisterInfo): Promise<{ error: string | null, status: number | undefined }> => {
		const response = await DineRewardsService.PostEditAccountInfo({ ...body });
		if (response.error) {
			const error = response.body?.message ?? 'Unexpected error';
			const status = response.status
			return { error, status };
		}
		return { error: null, status: undefined };
	},
	ClaimMissingPoint: async (payload: RequestClaimMissingPointsDTO): Promise<ResponseClaimMissingPointsDTO> => {
		const response = await DineRewardsService.ClaimMissingPoint(payload);
		if (response.error) {
			return {
				status: response.status,
				key: 'error',
				title: 'Connection Error',
				messageDetail: `We're currently experiencing issues connecting to our servers. Please try again later.

					If the issue keeps happening, contact us via email:
					<a href="mailto:dinerewards@bloominbrands.com" target="_blank"><b>dinerewards@bloominbrands.com</b></a>
					`,
			};
		}
		return response.body;
	},
};
